<script lang="ts" setup>
import { ref, computed, type PropType } from 'vue'

interface NextgenEventsItem {
  timestamp: string | Date
  eventUnit: string
  eventType: string
  totalCement: number
  totalAccelerator: number
  totalAdditive1: number
  totalAdditive2: number
  totalAdditive3: number
  totalAdditive4: number
  totalAdditive5: number
  totalAdditive6: number
  totalColloidal: number
  totalColloidalSlurry: number
  totalWater: number
  powerConsumptionskWh: number
}

const props = defineProps({
  events: {
    type: Array as PropType<NextgenEventsItem[]>,
    required: true
  }
})

const searchQuery = ref('')

function calculateIncrementStats(values: number[]) {
  const increments = []
  
  for (let i = 1; i < values.length; i++) {
    const diff = values[i]! - values[i - 1]!
    if (diff > 0) {
      increments.push(diff)
    }
  }
  
  if (increments.length === 0) return { avg: 0, max: 0 }
  
  const sum = increments.reduce((a, b) => a + b, 0)
  return {
    avg: sum / increments.length,
    max: Math.max(...increments)
  }
}

function getEventUnitColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('pump 1')) return 'info';
  if (t.includes('pump 2')) return 'success';
  if (t.includes('pump 3')) return 'error';
  if (t.includes('pump 4')) return 'warning';
  return 'default';
}
function getEventTypeColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('emergency')) return 'error';
  if (t.includes('reached')) return 'success';
  if (t.includes('pump start') || t.includes('pump stop')) return 'secondary';
  if (t.includes('hole change')) return 'info';
  if (t.includes('low level')) return 'warning';
  return 'default';
}

const cementStats = computed(() => calculateIncrementStats(filteredEvents.value.map(e => e.totalCement)))

const fmt = (val: number | undefined | null) => {
  if (val === undefined || val === null) return '0.0'
  return val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

const filteredEvents = computed(() => {
  if (!searchQuery.value) return props.events
  
  const q = searchQuery.value.toLowerCase()
  return props.events.filter(evt => 
    (evt.eventUnit || '').toLowerCase().includes(q) || 
    (evt.eventType || '').toLowerCase().includes(q)
  )
})

const headers = [
  { title: 'Timestamp', key: 'timestamp', align: 'start' },
  { title: 'Unit', key: 'eventUnit', align: 'start' },
  { title: 'Event Type', key: 'eventType', align: 'start' },
  { title: 'Total Cement', key: 'totalCement', align: 'end' },
  { title: 'Total Additive 1', key: 'totalAdditive1', align: 'end' },
  { title: 'Total Additive 2', key: 'totalAdditive2', align: 'end' },
  { title: 'Total Additive 3', key: 'totalAdditive3', align: 'end' },
  { title: 'Total Colloidal', key: 'totalColloidal', align: 'end' },
  { title: 'Total Slurry', key: 'totalColloidalSlurry', align: 'end' },
  { title: 'Total Water', key: 'totalWater', align: 'end' },
  { title: 'Power (kWh)', key: 'powerConsumptionskWh', align: 'end' },
] as const;
</script>

<template>
  <v-container fluid>
    <!-- Cement Stats Card -->
    <v-row class="mb-6">
      <v-col cols="12" md="4">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Cement Stats
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center mb-4 border-b pb-2">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Avg Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cementStats.avg) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Max Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cementStats.max) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- System Event Log -->
    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <span class="text-subtitle-1 font-weight-bold">System Event Log ({{ filteredEvents.length }})</span>
        <v-text-field
          v-model="searchQuery"
          placeholder="Filter by Unit or Event..."
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="outlined"
          hide-details
          bg-color="white"
          style="min-width: 250px;"
        ></v-text-field>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="filteredEvents"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.timestamp="{ item }">
          <span class="text-body-2 text-medium-emphasis" style="white-space: nowrap;">
            {{ new Date(item.timestamp).toLocaleString() }}
          </span>
        </template>

        <template v-slot:item.eventUnit="{ item }">
          <v-chip :color="getEventUnitColor(item.eventUnit)" size="small" variant="tonal" class="font-weight-bold">
            {{ item.eventUnit }}
          </v-chip>
        </template>

        <template v-slot:item.eventType="{ item }">
          <v-chip
            :color="getEventTypeColor(item.eventType)"
            size="small"
            variant="tonal"
            class="font-weight-bold text-uppercase"
            label
          >
            {{ item.eventType }}
          </v-chip>
        </template>

        <template v-slot:item.powerConsumptionskWh="{ item }">
          <span class="font-weight-bold text-green-darken-1 font-monospace">{{ item.powerConsumptionskWh }}</span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.gap-4 {
  gap: 1rem;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>